import * as React from 'react'
// Named import on purpose: the default export crashes the production build.
import { Icon as MdiIcon } from '@mdi/react'
import { cn } from '../../lib/utils'

/**
 * `AppIcon` — the only way the app renders an MDI glyph (spec 2026-09-17-02).
 *
 * Two things it fixes over calling `@mdi/react` directly:
 *
 *  - **A size grid.** The raw component takes a `rem` multiplier, which is how
 *    the app ended up with five ungridded sizes (14.4, 16.8, 19.2, 12, 24 px)
 *    sitting next to 16 px lucide icons. `AppIcon` takes a NAME off a six-step
 *    scale and emits pixels.
 *  - **A colour.** A standalone MDI icon is `--icon` blue, not the surrounding
 *    text colour. Semantic colour is passed as a `className` and wins, because
 *    `cn()` (tailwind-merge) keeps the caller's `text-*`. On a coloured surface
 *    — a primary button, a badge, a gradient tile — the surface forces
 *    inheritance from the outside with `[&_svg]:text-current`, so no call site
 *    has to think about it.
 */

export const APP_ICON_SIZES = {
  /** inside badges, inline in 12 px text */
  xs: 14,
  /** buttons, inputs, table cells, tabs */
  sm: 16,
  /** sidebar navigation, list rows, section titles */
  md: 20,
  /** glyph inside a `lg` tile, page-level actions */
  lg: 24,
  /** glyph inside an `xl` tile */
  xl: 32,
  /** empty states without a tile */
  '2xl': 48,
} as const

export type AppIconSize = keyof typeof APP_ICON_SIZES

export type AppIconTone = 'default' | 'muted' | 'inherit'

export const APP_ICON_TONE_CLASSES: Record<AppIconTone, string> = {
  default: 'text-icon',
  muted: 'text-icon-muted',
  inherit: 'text-current',
}

type MdiIconProps = React.ComponentProps<typeof MdiIcon>

export interface AppIconProps
  extends Omit<MdiIconProps, 'size' | 'color' | 'path'> {
  path: string
  size?: AppIconSize
  tone?: AppIconTone
}

function AppIcon({
  path,
  size = 'sm',
  tone = 'default',
  className,
  ...props
}: AppIconProps) {
  return (
    <MdiIcon
      path={path}
      size={`${APP_ICON_SIZES[size]}px`}
      className={cn(APP_ICON_TONE_CLASSES[tone], className)}
      {...props}
    />
  )
}

/**
 * The nearest named size to a pixel value, for the handful of call sites whose
 * glyph is sized off a runtime dimension (an avatar's side, say) rather than
 * chosen by a designer. Prefer a literal name everywhere else.
 */
export function nearestAppIconSize(px: number): AppIconSize {
  const names = Object.keys(APP_ICON_SIZES) as AppIconSize[]
  return names.reduce((best, name) =>
    Math.abs(APP_ICON_SIZES[name] - px) < Math.abs(APP_ICON_SIZES[best] - px)
      ? name
      : best
  )
}

export { AppIcon }
